import { useApiQuery } from "@gladis/api-client";
import { formatEur, type ExpensesScheduleDto, type SchedulePeriod } from "@gladis/shared";
import { Button, ErrorState, LoadingState, Panel } from "@gladis/ui";
import { errorSentence, formatMonth, tRich, useT } from "@gladis/ui/i18n";
import { useState, type ReactNode } from "react";

import { ComingUpList } from "./ComingUpList.js";
import { expenseScopeOf } from "./ExpenseFilters.js";
import { EXPENSES_KEY } from "./ExpenseList.js";
import { INTERVAL_OPTIONS } from "./intervals.js";
import { ScheduleChart } from "./ScheduleChart.js";

/**
 * The schedule read (feature 024): what is still to go out, the "Next due" dates and the anchors in force.
 *
 * KEYED UNDER `EXPENSES_KEY`, so every mutation that already refreshes the Expenses page refreshes this too —
 * an expense saved here, a cost added on a vehicle's page, a mortgage's "Who pays". That is the whole of the
 * invalidation story, and it holds by structure rather than by a list somebody has to keep.
 *
 * ALWAYS REFETCHED ON MOUNT, for `useExpenses`' reason: this payload carries mortgage instalments, which the
 * Properties page writes and this page cannot observe changing. The Dashboard card uses the SAME key, so the
 * two can never show different figures from one cache.
 */
export function useExpensesSchedule(
  period: SchedulePeriod,
  member?: string,
): ReturnType<typeof useApiQuery<ExpensesScheduleDto>> {
  const query = new URLSearchParams({ period });
  if (member) query.set("member", member);
  return useApiQuery<ExpensesScheduleDto>(
    [...EXPENSES_KEY, "schedule", period, member ?? "household"],
    `/expenses/schedule?${query.toString()}`,
    { staleTime: 0, refetchOnMount: "always" },
  );
}

const PERIOD_STORAGE_KEY = "gladis.expenses.schedulePeriod";
const PERIODS: readonly SchedulePeriod[] = ["week", "month", "year"];
// Spelled out rather than built from the period, so the catalogue's both-ways check can see each key used.
const PERIOD_LABEL: Record<SchedulePeriod, string> = {
  week: "exp.schedule.period.week",
  month: "exp.schedule.period.month",
  year: "exp.schedule.period.year",
};

/** The remembered toggle — a per-viewer convenience, so it may fail silently and default to the month. */
function rememberedPeriod(): SchedulePeriod {
  try {
    const stored = window.localStorage.getItem(PERIOD_STORAGE_KEY);
    return PERIODS.find((period) => period === stored) ?? "month";
  } catch {
    return "month";
  }
}

function remember(period: SchedulePeriod): void {
  try {
    window.localStorage.setItem(PERIOD_STORAGE_KEY, period);
  } catch {
    // Private mode or blocked storage: the toggle still works, it is only not remembered.
  }
}

const euros = (value: number) => formatEur(value);

/**
 * "Still to go out" — the Expenses page section between the stat cards and the trend (FR-010).
 *
 * A SCHEDULE, NOT A RECORD (FR-019): GLADIS has no bank feed, so every word here is "scheduled" or "still to go
 * out", never "paid". CASH DUE, NOT MONTHLY EQUIVALENTS (FR-017) — the figures above it on the page answer a
 * different question about the same expenses, and the only averages in this section are the "No due date"
 * block's, which always carry ≈.
 *
 * The page's "whose cost" filter governs it (FR-031); the archived toggle and the search do not (FR-033) —
 * they are not passed in.
 */
export function PaymentScheduleSection({
  member,
  members = [],
  onSetDate,
}: {
  member?: string;
  members?: readonly { userId: string; displayName: string }[];
  /** Opens the right editor for a row in the block — the expense form, or "Who pays" for a mortgage. */
  onSetDate: (rowKey: string) => void;
}): ReactNode {
  const t = useT();
  const [period, setPeriod] = useState<SchedulePeriod>(rememberedPeriod);
  const { data, isPending, error } = useExpensesSchedule(period, member);
  const scope = expenseScopeOf(member === undefined ? undefined : { member }, members);

  const title =
    data === undefined
      ? t("exp.schedule.titleLoading")
      : period === "week"
        ? t("exp.schedule.titleWeek")
        : period === "month"
          ? t("exp.schedule.titleMonth", {
              month: formatMonth(Number(data.asOf.slice(0, 4)), Number(data.asOf.slice(5, 7))),
            })
          : t("exp.schedule.titleYear", { year: data.asOf.slice(0, 4) });

  const toggle = (
    <div className="flex flex-wrap gap-2" role="group" aria-label={t("exp.schedule.periodLabel")}>
      {PERIODS.map((option) => (
        <Button
          key={option}
          variant={option === period ? "primary" : "outline"}
          aria-pressed={option === period}
          onClick={() => {
            setPeriod(option);
            remember(option);
          }}
        >
          {t(PERIOD_LABEL[option])}
        </Button>
      ))}
    </div>
  );

  return (
    <div id="still-to-go-out" data-testid="still-to-go-out">
      <Panel
        title={title}
        description={
          scope.kind === "member"
            ? t("exp.schedule.scopeMember", { name: scope.name })
            : scope.kind === "joint"
              ? t("exp.schedule.scopeJoint")
              : t("exp.schedule.scopeHousehold")
        }
        aside={toggle}
      >
        {isPending ? (
          <LoadingState>{t("exp.schedule.loading")}</LoadingState>
        ) : error || data === undefined ? (
          <ErrorState>{t("exp.schedule.loadFailed", { reason: errorSentence(error) })}</ErrorState>
        ) : (
          <ScheduleBody data={data} period={period} onSetDate={onSetDate} />
        )}
      </Panel>
    </div>
  );
}

function ScheduleBody({
  data,
  period,
  onSetDate,
}: {
  data: ExpensesScheduleDto;
  period: SchedulePeriod;
  onSetDate: (rowKey: string) => void;
}): ReactNode {
  const t = useT();
  const total = `${data.totals.isEstimate ? "≈ " : ""}${euros(data.totals.periodTotalInEuros)}`;

  return (
    <>
      <p className="mb-4 text-sm text-muted" data-testid="schedule-headline">
        {tRich("exp.schedule.headline", {
          remaining: (
            <span className="text-2xl font-semibold text-ink tabular-nums">
              {euros(data.totals.remainingInEuros)}
            </span>
          ),
          total: <span className="tabular-nums">{total}</span>,
        })}
      </p>

      {data.mortgageUnavailable ? (
        <p className="mb-4 text-sm text-muted">{t("exp.schedule.mortgageUnavailable")}</p>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="min-w-0 lg:col-span-2">
          <ScheduleChart data={data} period={period} />
        </div>

        <div className="flex min-w-0 flex-col gap-6">
          <ComingUpList data={data} period={period} />
          <UndatedBlock data={data} onSetDate={onSetDate} />
        </div>
      </div>
    </>
  );
}

/**
 * Expenses with no due date (FR-021 to FR-024): never dropped, never guessed — each at its average for the
 * period, marked ≈, so the chart's start plus this block is the period total the headline states.
 */
function UndatedBlock({
  data,
  onSetDate,
}: {
  data: ExpensesScheduleDto;
  onSetDate: (rowKey: string) => void;
}): ReactNode {
  const t = useT();
  if (data.undated.length === 0) return null;

  return (
    <section aria-labelledby="schedule-undated" data-testid="schedule-undated">
      <header className="mb-2 flex items-baseline justify-between gap-3">
        <h3 id="schedule-undated" className="font-semibold">
          {t("exp.schedule.undated.heading")}
        </h3>
        <span className="text-sm tabular-nums text-muted">
          ≈ {euros(data.totals.undatedEstimateInEuros)}
        </span>
      </header>
      <p className="mb-2 text-xs text-muted">{t("exp.schedule.undated.hint")}</p>
      {/* Three rows tall, then it scrolls: a household that has dated nothing yet would otherwise push the
          chart's own column far below the fold. 31 = three 36px rows (the Button's h-9), two gap-1s and the
          py-1 that keeps a focused button's ring from being clipped by the overflow. */}
      <ul className="-mx-1 flex max-h-31 flex-col gap-1 overflow-y-auto px-1 py-1">
        {data.undated.map((row) => {
          const interval = INTERVAL_OPTIONS.find((entry) => entry.value === row.interval);
          return (
            <li key={row.rowKey} className="flex items-center justify-between gap-3 text-sm">
              <span className="min-w-0 truncate">
                {row.description}
                {interval ? <span className="text-muted"> · {t(interval.labelKey)}</span> : null}
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <span className="tabular-nums text-muted">≈ {euros(row.estimateInEuros)}</span>
                <Button
                  variant="outline"
                  aria-label={t("exp.schedule.undated.setFor", { name: row.description })}
                  onClick={() => {
                    onSetDate(row.rowKey);
                  }}
                >
                  {t("exp.schedule.undated.set")}
                </Button>
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
